import { useEffect, useState } from "react";
import { getAllTestimonials, createTestimonial, updateTestimonial, deleteTestimonial, uploadSiteImage } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, X, Camera, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

const empty = { name: "", role: "", quote: "", avatar_url: "", published: true };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  async function load() {
    const data = await getAllTestimonials();
    setItems(data || []);
  }
  useEffect(() => { load(); }, []);

  function startEdit(t) { setEditing(t.id); setForm({ name: t.name, role: t.role || "", quote: t.quote, avatar_url: t.avatar_url || "", published: !!t.published }); }
  function startNew() { setEditing("new"); setForm(empty); }
  function cancel() { setEditing(null); setForm(empty); }

  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadSiteImage(file, "testimonials");
      setForm(prev => ({ ...prev, avatar_url: url }));
      toast.success("Foto enviada.");
    } catch (err) {
      console.error(err);
      toast.error("Falha ao enviar foto.");
    } finally {
      setUploading(false);
    }
  }

  function handleRemoveImage() {
    setForm(prev => ({ ...prev, avatar_url: "" }));
  }

  async function save() {
    try {
      if (editing === "new") {
        await createTestimonial(form);
        toast.success("Depoimento criado.");
      } else {
        await updateTestimonial(editing, form);
        toast.success("Depoimento atualizado.");
      }
      cancel();
      load();
    } catch (err) { console.error(err); toast.error("Falha ao salvar."); }
  }

  async function remove() {
    if (!itemToDelete) return;
    try {
      await deleteTestimonial(itemToDelete);
      toast.success("Removido.");
      load();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao remover depoimento.");
    } finally {
      setItemToDelete(null);
    }
  }

  return (
    <div data-testid="admin-testimonials-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="font-serif text-3xl text-softblack">Depoimentos</h2>
          <p className="text-warmgray text-sm font-light mt-1">O que os clientes dizem sobre seu trabalho.</p>
        </div>
        <button data-testid="testimonial-new" onClick={startNew} className="btn-primary !px-6 !py-3 !text-[11px] w-full sm:w-auto flex items-center justify-center">
          <Plus size={16} strokeWidth={1.5} className="mr-2" /> Novo Depoimento
        </button>
      </div>

      {/* Form for creating new testimonials (always at the top) */}
      {editing === "new" && (
        <div data-testid="testimonial-form" className="bg-white border border-fendi p-10 mb-12 shadow-sm animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="flex items-center justify-between mb-8 border-b border-fendi/30 pb-4">
            <p className="eyebrow !text-xs tracking-widest text-softblack/60">Novo Depoimento</p>
            <button onClick={cancel} className="text-warmgray hover:text-softblack transition-colors"><X size={20} /></button>
          </div>
          
          <div className="space-y-8">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="md:col-span-1">
                <span className="eyebrow mb-3 block">Foto</span>
                <div className="relative group w-24 h-24 rounded-full bg-fendi/10 overflow-hidden border border-fendi/30 mx-auto md:mx-0">
                  {form.avatar_url ? (
                    <>
                      <img src={form.avatar_url} alt="preview" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 text-white z-10">
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveImage(); }} className="mb-2 hover:text-destructive transition-colors">
                          <Trash2 size={16} />
                        </button>
                        <label className="cursor-pointer flex items-center justify-center">
                          <Camera size={16} />
                          <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                        </label>
                      </div>
                    </>
                  ) : (
                    <label className="w-full h-full flex items-center justify-center text-warmgray cursor-pointer">
                      <Camera size={24} strokeWidth={1} />
                      <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                    </label>
                  )}
                  {uploading && <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white z-20"><Loader2 className="animate-spin" /></div>}
                </div>
              </div>

              <div className="md:col-span-3 space-y-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <Field label="Nome do Cliente" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="testimonial-input-name" />
                  <Field label="Cargo / Empresa / Local" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="testimonial-input-role" />
                </div>

                <div className="block">
                  <span className="eyebrow mb-3 block">Depoimento</span>
                  <textarea data-testid="testimonial-input-quote" rows={5} className="editorial-field w-full leading-relaxed" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} placeholder="Escreva aqui o depoimento do cliente..." />
                </div>

                <label className="flex items-center gap-3 h-[52px] px-4 border border-fendi/20 bg-fendi/5 cursor-pointer hover:bg-fendi/10 transition-colors w-fit">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="testimonial-input-published" className="accent-softblack" />
                  <span className="text-sm font-light text-softblack">Publicar no Site</span>
                </label>
              </div>
            </div>

            <div className="pt-6 border-t border-fendi/30 flex justify-end">
              <button onClick={save} data-testid="testimonial-save" className="btn-primary !px-10">Salvar Depoimento</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8">
        {items.map((t) => (
          <div key={t.id} data-testid={`testimonial-card-${t.id}`} className="group bg-white border border-fendi hover:shadow-md transition-all duration-300 flex flex-col relative">
            {/* Edit form for this specific item */}
            {editing === t.id ? (
              <div className="p-8 bg-champagne/20 flex-grow animate-in fade-in duration-200">
                <div className="flex items-center justify-between mb-6 border-b border-fendi/20 pb-3">
                  <p className="eyebrow !text-[10px] text-softblack/50">Editando Depoimento</p>
                  <button onClick={cancel} className="text-warmgray hover:text-softblack"><X size={16} /></button>
                </div>
                
                <div className="space-y-6">
                  <div className="flex justify-center mb-6">
                    <div className="relative group w-20 h-20 rounded-full bg-fendi/10 overflow-hidden border border-fendi/30">
                      {form.avatar_url ? (
                        <>
                          <img src={form.avatar_url} alt="preview" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 text-white z-10">
                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveImage(); }} className="mb-1 hover:text-destructive transition-colors">
                              <Trash2 size={14} />
                            </button>
                            <label className="cursor-pointer flex items-center justify-center">
                              <Camera size={14} />
                              <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                            </label>
                          </div>
                        </>
                      ) : (
                        <label className="w-full h-full flex items-center justify-center text-warmgray cursor-pointer">
                          <Camera size={20} strokeWidth={1} />
                          <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                        </label>
                      )}
                      {uploading && <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white z-20"><Loader2 className="animate-spin" size={16} /></div>}
                    </div>
                  </div>

                  <div className="grid gap-6">
                    <Field label="Nome" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid={`testimonial-input-name-${t.id}`} />
                    <Field label="Cargo / Local" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid={`testimonial-input-role-${t.id}`} />
                  </div>

                  <div className="block">
                    <span className="eyebrow mb-2 block">Citação</span>
                    <textarea data-testid={`testimonial-input-quote-${t.id}`} rows={4} className="editorial-field !py-3 w-full text-sm leading-relaxed" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} />
                  </div>

                  <label className="flex items-center gap-3 py-2 cursor-pointer">
                    <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid={`testimonial-input-published-${t.id}`} className="accent-softblack" />
                    <span className="text-[11px] eyebrow text-softblack uppercase tracking-wider">Publicado</span>
                  </label>

                  <div className="pt-4">
                    <button onClick={save} data-testid={`testimonial-save-${t.id}`} className="btn-primary w-full !py-3 !text-[10px] shadow-sm">Confirmar Alterações</button>
                  </div>
                </div>
              </div>
            ) : (
              /* Display view for this item */
              <div className="p-8 flex flex-col h-full">
                <div className="flex items-start justify-between gap-6 mb-6">
                  <div className="flex gap-4 items-center">
                    {t.avatar_url ? (
                      <img src={t.avatar_url} alt={t.name} className="w-12 h-12 rounded-full object-cover border border-fendi/30" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-fendi/10 border border-fendi/20 flex items-center justify-center">
                        <span className="text-fendi font-serif text-lg">{t.name.charAt(0)}</span>
                      </div>
                    )}
                    <div>
                      <p className="font-serif text-xl text-softblack">{t.name}</p>
                      {t.role && <p className="eyebrow !text-[9px] mt-0.5 text-warmgray">{t.role}</p>}
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <button onClick={() => startEdit(t)} data-testid={`testimonial-edit-${t.id}`} className="text-warmgray hover:text-softblack transition-colors" title="Editar">
                      <Pencil size={14} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => setItemToDelete(t.id)} data-testid={`testimonial-delete-${t.id}`} className="text-warmgray hover:text-destructive transition-colors" title="Excluir">
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
                
                <div className="flex-grow">
                  <p className="text-warmgray font-light text-sm leading-relaxed italic border-l-2 border-fendi/20 pl-4 py-1">
                    "{t.quote}"
                  </p>
                </div>
                
                <div className="mt-8 flex items-center justify-between border-t border-fendi/10 pt-4">
                  <span className={`eyebrow !text-[9px] px-2 py-0.5 rounded-full ${t.published ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-warmgray/5 text-warmgray border border-warmgray/10'}`}>
                    {t.published ? "Publicado" : "Oculto"}
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center text-warmgray font-light py-20 bg-fendi/5 border border-dashed border-fendi">
            Nenhum depoimento cadastrado.
          </div>
        )}
      </div>

      <AlertDialog open={!!itemToDelete} onOpenChange={() => setItemToDelete(null)}>
        <AlertDialogContent className="bg-white border border-fendi rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-3xl text-softblack">Excluir Depoimento?</AlertDialogTitle>
            <AlertDialogDescription className="text-warmgray text-base font-light leading-relaxed">
              Esta ação removerá permanentemente o depoimento. Você não poderá recuperar estes dados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 gap-4">
            <AlertDialogCancel className="btn-ghost !border-fendi/20 !py-3 !px-8 text-[11px] uppercase tracking-widest">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-white hover:bg-destructive/90 !py-3 !px-8 text-[11px] uppercase tracking-widest rounded-none">
              Confirmar Exclusão
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="eyebrow mb-3 block">{label}</span>
      <input data-testid={testid} className="editorial-field w-full" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
